import { useCallback } from 'react';

import { CustomWindow } from '../../../../types';
import { whenYouTubeApiReady } from '../../../../utils/youtube-iframe-api';
import { sendVideoPlaybackToChatbot } from '../../iframeMessageHandler';

declare const window: CustomWindow;

type YouTubePlayer = {
  getDuration?: () => number;
  getPlayerState?: () => number;
};

type ReadyEvent = { target?: YouTubePlayer };
type StateChangeEvent = { data: number; target?: YouTubePlayer };

const playerByIframe = new WeakMap<HTMLIFrameElement, YouTubePlayer | null>();

const toDurationSeconds = (player?: YouTubePlayer): number | undefined => {
  const duration = player?.getDuration?.();
  return duration && duration > 0 ? Math.round(duration) : undefined;
};

export const useReportPlaybackToChatbot = (
  videoId: string | undefined,
): ((iframe: HTMLIFrameElement | null) => void) =>
  useCallback(
    (iframe: HTMLIFrameElement | null) => {
      if (!iframe || !videoId || playerByIframe.has(iframe)) {
        return;
      }
      playerByIframe.set(iframe, null);

      const report = (playerState: number, player?: YouTubePlayer) =>
        sendVideoPlaybackToChatbot(videoId, playerState, toDurationSeconds(player));

      void whenYouTubeApiReady().then(() => {
        if (!iframe.isConnected) {
          return;
        }
        try {
          const player = new window.YT.Player(iframe, {
            events: {
              onReady: ({ target }: ReadyEvent) => {
                const playerState = target?.getPlayerState?.();
                if (playerState !== undefined) {
                  report(playerState, target);
                }
              },
              onStateChange: ({ data, target }: StateChangeEvent) =>
                report(data, target),
            },
          });
          playerByIframe.set(iframe, player);
        } catch (error) {
          console.warn('Error attaching YouTube player for chatbot video:', error);
        }
      });
    },
    [videoId],
  );
